import {
  ArrowRightIcon,
  BannerAccent,
  FancyBanner,
  useSnoozedDismissal,
} from "@/components/molecules/FancyBanner/FancyBanner";
import {
  IS_ARCHIVE_EDITION,
  OTHER_EDITION_URL,
  SHOW_FC27_PROMO,
} from "@/config/game";
import { CoinIcon } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { topToPageSelector } from "@/redux/globalState/globalStateSlice";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { useSelector } from "react-redux";

/*
  Points the root deploy at the staged FC 27 deploy, in the same two shells as
  the club-import banner. The palette is green rather than blue so the two read
  as different offers when both are on the front page at once.

  Renders nothing in the FC 27 deploy itself — the nav banner already offers
  the way back there.
*/

const GREEN = "74,222,128";

/* One hue throughout, unlike the club-import banner's three blues. */
const ACCENT: BannerAccent = {
  base: GREEN,
  deep: GREEN,
  peak: "190,250,210",
  dot: GREEN,
  panelFrom: "rgba(36,42,38,.92)",
  panelTo: "rgba(18,22,19,.96)",
};

const DISMISSED_AT_KEY = "fc27BannerDismissedAt";

/*
  The CTA is a span, not the DS Button: the whole banner is an anchor, and a
  button inside a link is invalid. It therefore carries its own solid fill
  rather than the Button's gradient-border variables, which do nothing without
  the component that reads them.
*/
const CTA = cn(
  "shrink-0 rounded-[10px] font-semibold whitespace-nowrap",
  "bg-[rgb(110,231,140)] text-[#07240f]",
  "transition-[transform,filter,box-shadow] duration-[180ms] ease-out",
  "hover:-translate-y-px hover:brightness-105",
  "hover:shadow-[0_6px_18px_-8px_rgba(74,222,128,.7)]",
);

/** Stands in for the club-import banner's icon: the edition, set as a badge. */
const EditionBadge = ({ compact }: { compact?: boolean }) => {
  const { t } = useTranslation("nav");
  return (
    <span
      className={cn(
        "font-oswald font-semibold uppercase leading-none tracking-[0.04em]",
        "text-[rgb(134,239,172)]",
        compact ? "text-[13px]" : "text-[15px]",
      )}
    >
      {t("fc27Banner.badge")}
    </span>
  );
};

const Headline = () => (
  <Trans
    ns="nav"
    i18nKey="fc27Banner.headline"
    components={{
      live: <em className="not-italic text-[rgb(134,239,172)]" />,
    }}
  />
);

type Props = {
  variant?: "desktop" | "compact";
  className?: string;
};

export const Fc27Banner = ({ variant = "desktop", className }: Props) => {
  const { t } = useTranslation("nav");
  const { hidden, dismissing, dismiss, handleTransitionEnd } =
    useSnoozedDismissal(DISMISSED_AT_KEY);
  /*
    The compact strip is sticky, and so is the chrome above it: the features
    banner and the mobile nav. Sticking at top-0 parks it underneath them. The
    store already tracks how much of that chrome is showing, so the offset
    follows it rather than hardcoding a height that changes with the banner.
  */
  const topToPage = useSelector(topToPageSelector);

  // Not promoting, already in the FC 27 deploy, snoozed, or dismissed.
  if (!SHOW_FC27_PROMO || !IS_ARCHIVE_EDITION || hidden) return null;

  const compact = variant === "compact";

  return (
    <FancyBanner
      accent={ACCENT}
      variant={variant}
      href={OTHER_EDITION_URL}
      icon={<EditionBadge compact={compact} />}
      title={<Headline />}
      description={
        compact ? (
          <div className="flex flex-row gap-x-1 items-center">
            <CoinIcon size={12}></CoinIcon>
            {t("fc27Banner.descriptionCompact")}
          </div>
        ) : (
          <div className="flex flex-row gap-x-1 items-center">
            <CoinIcon size={20}></CoinIcon>
            {t("fc27Banner.description")}
          </div>
        )
      }
      cta={
        compact ? (
          <span className={cn(CTA, "px-3 py-1.5 text-[12px]")}>
            {t("fc27Banner.explore")}
          </span>
        ) : (
          <span className={cn(CTA, "px-5 py-3 text-sm")}>
            <span className="flex items-center gap-[9px]">
              {t("fc27Banner.exploreFc27")}
              <ArrowRightIcon />
            </span>
          </span>
        )
      }
      dismissLabel={t("fc27Banner.dismiss")}
      onDismiss={dismiss}
      onTransitionEnd={handleTransitionEnd}
      dismissing={dismissing}
      className={cn(compact && topToPage.tailwindString, className)}
    />
  );
};
